<template>
  <Teleport to="body">
    <div
      v-if="typeof model === 'undefined' || model"
      class="fixed z-[9999] px-4 top-0 left-0 h-screen w-screen overflow-hidden bg-black/50 dark:bg-white/30 transition-opacity duration-500 flex flex-wrap items-center justify-center"
    >
      <Transition
        mode="out-in"
        enter-active-class="transition duration-500 transform"
        enter-from-class="opacity-0 translate-y-full"
        leave-active-class="duration-500 transform"
        leave-to-class="opacity-0 translate-y-full"
        appear
      >
        <div class="space-y-4 box-border z-10 max-w-[412px] flex flex-col w-full rounded-md bg-[#FDFDFD] py-4 px-4 text-[#666666] dark:text-[#FDFDFD] dark:bg-[#1D1D1D]">
          <slot>
            <div class="font-semibold text-[#212121] dark:text-[#FDFDFD]">{{ title || $t('feedback.attention') }}</div>
            <div v-if="message" class="text-sm leading-[18px] h-full break-words" v-html="message"></div>
            <UiInput
              v-if="props.withTextarea"
              v-model="inputResult"
              type="textarea"
              placeholder="Digite aqui..."
              class="w-full"
              rows="3"
            />
          </slot>
          <div class="flex flex-wrap gap-2 justify-end">
            <UiButton type="danger" outline  class="p-3 uppercase" @click="onCancel">
              {{ $t('button.cancel') }}
            </UiButton>
            <UiButton id="buttonConfirm" :type="props.typeConfirmButton ?? 'primary'" class="p-3 uppercase" @click="onConfirm">
              {{ textConfirmButton ?? $t('button.confirm') }}
            </UiButton>
          </div>
        </div>
      </Transition>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { nextTick, onMounted, ref } from 'vue'
import { useNotificationsStore } from '@/stores'

import UiButton from '@/components/ui/button.vue'
import UiInput from '@/components/ui/input.vue'

const props = defineProps<{
  id?: string
  title?: string
  message?: string
  complete?: Function
  cancel?: Function
  typeConfirmButton?: 'primary' | 'info' | 'success' | 'warning' | 'danger'
  textConfirmButton?: string
  withTextarea?: boolean
  immediate?: boolean
}>()

const model = defineModel<undefined | boolean>()
const notificationsStore = useNotificationsStore()
const inputResult = ref<string | undefined>()

const localPromise = {
  resolve: null as null | (() => void),
  reject: null as null | (() => void)
}

const dismiss = () => {
  if (props.id) {
    notificationsStore.dismissNewDialog(props.id)
  }

  model.value = false
}

const onConfirm = async () => {
  if (typeof props?.complete === 'undefined' || (typeof props?.complete === 'function' && (await props?.complete?.({ result: inputResult.value }) === false))) {
    if (localPromise.resolve) localPromise.resolve()
  }

  dismiss()
}

const onCancel = async () => {
  if (typeof props?.cancel === 'function' && (await props?.cancel?.() === false)) {
    if (localPromise.reject) localPromise.reject()
  }

  dismiss()
}

const open = () => {
  model.value = true

  return new Promise<void>((resolve, reject) => {
    localPromise.resolve = resolve
    localPromise.reject = reject
  })
}

onMounted(() => {
  if (typeof model.value === 'undefined' || props.immediate) {
    model.value = true

    nextTick(() => {
      document?.getElementById('buttonConfirm')?.focus()
    })
  }
})

defineExpose({
  open
})
</script>
